import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  MoveVertical, Activity, PauseCircle, AlertTriangle, Settings, RefreshCw, 
  Wifi, WifiOff, Clock, Play, Pause, CheckCircle, XCircle, DoorOpen, Cpu,
  ArrowUp, ArrowDown, Signal, Zap, ShieldAlert, Users, ThermometerSun, Search, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import ExportMenu from '../components/ExportMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapeamento legível dos alarmes (chave PLC -> label PT)
const ALARM_LABELS = {
  EQUIP1_AVARIA: 'Avaria',
  EQUIP1_AVARIA_ALARM: 'Avaria com Alarme',
  EQUIP1_PSG_PRESO: 'Passageiro Preso',
  EQUIP1_FALHA_ILUM: 'Falha Iluminação',
  EQUIP1_BOT_EMG_3S: 'Botão Emergência',
  EQUIP1_TEMP_MAQ: 'Temp. Casa Máquinas',
  EQUIP1_PORTA_50S: 'Porta Aberta >50s',
  INF_BAT_LOW: 'Bateria Baixa',
  INF_AC_NOK: 'Falha Tensão 230V',
};

const Elevadores = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [readings, setReadings] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [analiseOperacional, setAnaliseOperacional] = useState(null);
  
  // Auto-refresh settings
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  // Tabela de eventos filtrável (todos os dispositivos)
  const [eventReadings, setEventReadings] = useState([]); // raw readings de todos os devices
  const [eventHoras, setEventHoras] = useState(24);
  const [eventSearch, setEventSearch] = useState('');
  const [eventDevice, setEventDevice] = useState('all');
  const [eventOnlyAlarms, setEventOnlyAlarms] = useState(false);

  const hasDevices = devices.length > 0;

  // Carregar summary
  const loadSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/elevadores/summary`);
      const data = await response.json();
      if (data.success) {
        setSummary(data);
      }
    } catch (error) {
      console.error('Erro ao carregar summary:', error);
    }
  }, []);

  // Refresh - para dispositivos sem dados reais, faz refresh simulado
  const refreshReadings = useCallback(async (showToast = false) => {
    if (refreshing) return;
    setRefreshing(true);
    
    try {
      // Refresh para gerar dados simulados nos dispositivos sem Raspberry
      const response = await fetch(`${API_URL}/api/elevadores/refresh-all`, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        setLastRefresh(new Date());
        if (showToast) {
          toast.success(`Leituras atualizadas: ${data.refreshed}/${data.total}`);
        }
        await loadSummary();
      }
    } catch (error) {
      console.error('Erro ao atualizar:', error);
    }
    
    setRefreshing(false);
  }, [refreshing, loadSummary]);

  // Carregar histórico de leituras
  const loadReadingsHistory = useCallback(async () => {
    if (!selectedDevice) return;
    
    try {
      const response = await fetch(`${API_URL}/api/elevadores/readings/${selectedDevice}?horas=24`);
      const data = await response.json();
      setReadings(data.readings || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  }, [selectedDevice]);

  // Carregar leituras de TODOS os dispositivos para a tabela de eventos
  const loadAllEventReadings = useCallback(async (horas) => {
    if (!devices || devices.length === 0) {
      setEventReadings([]);
      return;
    }
    try {
      const all = [];
      for (const dev of devices) {
        try {
          const r = await fetch(`${API_URL}/api/elevadores/readings/${dev.id}?horas=${horas}`);
          const d = await r.json();
          (d.readings || []).forEach((reading) => {
            const estado = reading.estado || {};
            const entradas = reading.entradas || {};
            const hasAlarm = (
              estado.EQUIP1_AVARIA || estado.EQUIP1_AVARIA_ALARM ||
              estado.EQUIP1_PSG_PRESO || estado.EQUIP1_FALHA_ILUM ||
              estado.EQUIP1_BOT_EMG_3S || estado.EQUIP1_TEMP_MAQ ||
              estado.EQUIP1_PORTA_50S || estado.INF_BAT_LOW ||
              entradas.BOT_EMG_ELEV1 || entradas.FALHA_BAT_ELEV1
            );
            all.push({
              timestamp: reading.timestamp,
              elevator_id: reading.elevator_id,
              equipamento: reading.equipamento || dev.equipamento_id || '—',
              local: reading.local || dev.local,
              plc_ok: reading.plc_ok ?? false,
              falhas: reading.falhas ?? 0,
              em_movimento: estado.EQUIP1_MOV || entradas.MOV_ELEV1 || false,
              porta_aberta: entradas.PORTA_ELEV1 || estado.EQUIP1_PORTA_50S || false,
              fora_servico: estado.EQUIP1_FORA_SERV || entradas.FORA_SERV_ELEV1 || false,
              tem_alarme: !!hasAlarm,
              modo_normal: estado.EQUIP1_MOD_NORM ?? true,
              simulado: !!reading.simulado,
            });
          });
        } catch (e) { /* skip */ }
      }
      setEventReadings(all.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || '')));
    } catch (e) {
      console.error('Erro ao carregar tabela de eventos elevadores:', e);
    }
  }, [devices]);

  useEffect(() => {
    loadAllEventReadings(eventHoras);
  }, [devices, eventHoras, loadAllEventReadings]);

  // Carregar análise operacional
  const loadAnaliseOperacional = useCallback(async () => {
    if (!selectedDevice) return;
    
    try {
      const response = await fetch(`${API_URL}/api/elevadores/analise-operacional/${selectedDevice}`);
      const data = await response.json();
      if (data.success && data.disponivel) {
        setAnaliseOperacional(data);
      } else {
        setAnaliseOperacional(null);
      }
    } catch (error) {
      console.error('Erro ao carregar análise operacional:', error);
    }
  }, [selectedDevice]);

  // Carregar dados iniciais
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const devicesResponse = await fetch(`${API_URL}/api/elevadores/devices`);
      const devicesData = await devicesResponse.json();
      setDevices(devicesData || []);

      if (devicesData && devicesData.length > 0) {
        setSelectedDevice(devicesData[0].id);
        await loadSummary();
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  }, [loadSummary]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (selectedDevice) {
      loadReadingsHistory();
      loadAnaliseOperacional();
    }
  }, [selectedDevice, loadReadingsHistory, loadAnaliseOperacional]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && hasDevices) {
      setCountdown(refreshInterval);
      
      countdownRef.current = setInterval(() => {
        setCountdown(prev => prev <= 1 ? refreshInterval : prev - 1);
      }, 1000);
      
      intervalRef.current = setInterval(() => {
        loadSummary();
        loadReadingsHistory();
        loadAnaliseOperacional();
      }, refreshInterval * 1000);
      
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setCountdown(0);
    }
  }, [autoRefresh, refreshInterval, hasDevices, loadSummary, loadReadingsHistory]);

  // Totais
  const totals = useMemo(() => {
    if (summary) {
      return {
        total: summary.total_elevadores,
        emServico: summary.em_servico,
        emManutencao: summary.em_manutencao,
        comAlarmes: summary.com_alarmes,
        semComunicacao: summary.sem_comunicacao || 0,
      };
    }
    return { total: 0, emServico: 0, emManutencao: 0, comAlarmes: 0, semComunicacao: 0 };
  }, [summary]);

  // Estado de destaque para a tabela de eventos (set ao clicar num alarme)
  const [highlightTimestamp, setHighlightTimestamp] = useState(null);

  // Click num alarme ativo → procura origem na DB e foca a linha na tabela de eventos
  const handleAlarmClick = useCallback(async (deviceId, alarmKey) => {
    try {
      const res = await fetch(`${API_URL}/api/elevadores/alarme-origem/${deviceId}/${alarmKey}`);
      const data = await res.json();
      if (!data.success || !data.found) {
        toast.warning(`Sem registo histórico para "${ALARM_LABELS[alarmKey] || alarmKey}". O alarme pode estar a ser inferido sem leitura PLC válida.`);
        return;
      }
      // Filtrar tabela pelo dispositivo do alarme
      setEventDevice(deviceId);
      setEventOnlyAlarms(true);
      // Forçar janela larga o suficiente para incluir a leitura
      try {
        const ageDays = (Date.now() - new Date(data.timestamp).getTime()) / (1000 * 60 * 60 * 24);
        if (ageDays > 7) setEventHoras(168);
        else if (ageDays > 2) setEventHoras(168);
        else setEventHoras(48);
      } catch { /* ignore */ }
      setHighlightTimestamp(data.timestamp);
      // Scroll suave até à secção de eventos
      requestAnimationFrame(() => {
        const el = document.getElementById('elevadores-events-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      toast.success(`Origem encontrada: ${format(new Date(data.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: pt })}`);
    } catch (err) {
      toast.error(`Erro ao localizar origem: ${err.message}`);
    }
  }, []);

  // Agrupar leituras por 15 minutos para a tabela
  const readings15min = useMemo(() => {
    if (readings.length === 0) return [];
    
    const grouped = {};
    readings.forEach(reading => {
      const date = new Date(reading.timestamp);
      const minutes = Math.floor(date.getMinutes() / 15) * 15;
      date.setMinutes(minutes, 0, 0);
      const key = date.toISOString();
      
      if (!grouped[key]) {
        grouped[key] = { timestamp: key, reading: reading };
      } else {
        // Usar a leitura mais recente dentro do intervalo
        if (new Date(reading.timestamp) > new Date(grouped[key].reading.timestamp)) {
          grouped[key].reading = reading;
        }
      }
    });
    
    return Object.values(grouped)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 96); // 24h * 4 = 96 intervalos
  }, [readings]);

  // Verificar se uma leitura é real
  const isRealReading = (reading) => !reading.simulado;

  // Obter valor de registo de uma leitura (compatível com ambos os formatos)
  const getRegVal = (reading, key) => {
    if (reading.entradas && key in reading.entradas) return reading.entradas[key];
    if (reading.estado && key in reading.estado) return reading.estado[key];
    if (reading.saidas && key in reading.saidas) return reading.saidas[key];
    if (reading.registos && key in reading.registos) return reading.registos[key];
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="elevadores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MoveVertical className="h-6 w-6 text-purple-500" />
            Elevadores
          </h1>
          <p className="text-gray-500">Monitorização de elevadores e autómatos</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Badge data-testid="device-count-badge" className={hasDevices ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
            {hasDevices ? (
              <><Wifi className="h-3 w-3 mr-1" /> {devices.length} Elevador{devices.length > 1 ? 'es' : ''}</>
            ) : (
              <><WifiOff className="h-3 w-3 mr-1" /> Sem Dispositivos</>
            )}
          </Badge>
          <Button data-testid="config-btn" variant="outline" size="sm" onClick={() => navigate('/configuracoes/elevadores')}>
            <Settings className="h-4 w-4 mr-2" />
            Configurar
          </Button>
        </div>
      </div>

      {/* Auto-Refresh Controls */}
      {hasDevices && (
        <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch id="auto-refresh" checked={autoRefresh} onCheckedChange={setAutoRefresh} data-testid="auto-refresh-toggle" />
                  <Label htmlFor="auto-refresh" className="flex items-center gap-2 cursor-pointer">
                    {autoRefresh ? <Play className="h-4 w-4 text-green-600" /> : <Pause className="h-4 w-4 text-gray-400" />}
                    Atualização Automática
                  </Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Intervalo:</Label>
                  <Select value={refreshInterval.toString()} onValueChange={(v) => setRefreshInterval(parseInt(v))}>
                    <SelectTrigger className="w-[120px] h-8" data-testid="refresh-interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 segundos</SelectItem>
                      <SelectItem value="30">30 segundos</SelectItem>
                      <SelectItem value="60">1 minuto</SelectItem>
                      <SelectItem value="120">2 minutos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {autoRefresh && countdown > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    Próxima: <span className="font-mono font-semibold">{countdown}s</span>
                  </div>
                )}
                
                {lastRefresh && (
                  <div className="text-sm text-gray-500">
                    Última: {format(lastRefresh, 'HH:mm:ss', { locale: pt })}
                  </div>
                )}
                
                <Button data-testid="refresh-btn" variant="outline" size="sm" onClick={() => { refreshReadings(true); loadReadingsHistory(); }} disabled={refreshing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cards de Resumo */}
      {hasDevices && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Elevadores</p>
                  <p className="text-2xl font-bold text-purple-600" data-testid="total-elevadores">{totals.total}</p>
                </div>
                <MoveVertical className="h-8 w-8 text-purple-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Em Serviço</p>
                  <p className="text-2xl font-bold text-green-600" data-testid="em-servico">{totals.emServico}</p>
                  <p className="text-xs text-gray-400">A funcionar normalmente</p>
                </div>
                <Activity className="h-8 w-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Em Manutenção</p>
                  <p className="text-2xl font-bold text-yellow-600" data-testid="em-manutencao">{totals.emManutencao}</p>
                  <p className="text-xs text-gray-400">Preventiva ou corretiva</p>
                </div>
                <PauseCircle className="h-8 w-8 text-yellow-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Com Alarmes</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="com-alarmes">{totals.comAlarmes}</p>
                  <p className="text-xs text-gray-400">Alarmes do PLC</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Sem Comunicação</p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="sem-comunicacao">{totals.semComunicacao}</p>
                  <p className="text-xs text-gray-400">Comunicador/PLC offline</p>
                </div>
                <WifiOff className="h-8 w-8 text-orange-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Estado dos Elevadores */}
      {hasDevices && summary?.devices && summary.devices.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MoveVertical className="h-5 w-5 text-purple-500" />
              Estado dos Elevadores
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {summary.devices.map((device) => (
                <Card 
                  key={device.id}
                  data-testid={`elevator-card-${device.id}`}
                  className={`border cursor-pointer transition-all ${selectedDevice === device.id ? 'ring-2 ring-purple-500' : ''} ${device.tem_alarme ? 'border-red-300 bg-red-50' : device.fora_servico ? 'border-gray-300 bg-gray-50' : 'border-gray-200'}`}
                  onClick={() => setSelectedDevice(device.id)}
                >
                  <CardContent className="pt-4">
                    {/* Header do card */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-medium text-gray-900">{device.local}</p>
                        <p className="text-sm text-gray-500">{device.descricao || 'Elevador'}</p>
                        {device.equipamento_id && (
                          <p className="text-xs text-gray-400 font-mono mt-1">{device.equipamento_id}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {/* Badge do estado do elevador */}
                        {!device.elevador_estado_disponivel ? (
                          <Badge className="bg-gray-200 text-gray-700">Estado Indeterminado</Badge>
                        ) : device.fora_servico ? (
                          <Badge className="bg-red-100 text-red-700">Fora Serviço</Badge>
                        ) : device.em_manutencao ? (
                          <Badge className="bg-yellow-100 text-yellow-700">Manutenção</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">Em Serviço</Badge>
                        )}
                        <Badge variant="outline" className={`text-xs ${device.simulado ? '' : 'border-green-500 text-green-700'}`}>
                          {device.simulado ? 'Simulado' : 'Tempo Real'}
                        </Badge>
                      </div>
                    </div>

                    {/* Estado do Comunicador e PLC - só para dados reais */}
                    {!device.simulado && (
                      <div className="space-y-1.5 mb-3">
                        {/* Linha 1: Comunicador */}
                        <div className={`flex items-center gap-3 p-2 rounded border ${device.comunicador_online ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'}`}>
                          <div className="flex items-center gap-1.5">
                            {device.comunicador_online ? (
                              <Wifi className="h-3.5 w-3.5 text-green-600" />
                            ) : (
                              <WifiOff className="h-3.5 w-3.5 text-red-600" />
                            )}
                            <span className={`text-xs font-medium ${device.comunicador_online ? 'text-green-700' : 'text-red-700'}`}>
                              Comunicador {device.comunicador_online ? 'Online' : 'Offline'}
                            </span>
                          </div>
                          {device.comunicador_ultima_leitura && (
                            <span className="text-xs text-gray-400 ml-auto">
                              {format(new Date(device.comunicador_ultima_leitura), 'dd/MM HH:mm:ss', { locale: pt })}
                            </span>
                          )}
                        </div>

                        {/* Linha 2: PLC - só se comunicador online */}
                        {device.comunicador_online && (
                          <div className={`flex items-center gap-3 p-2 rounded border ${device.plc_ok ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-300'}`}>
                            <div className="flex items-center gap-1.5">
                              <Signal className={`h-3.5 w-3.5 ${device.plc_ok ? 'text-green-600' : 'text-orange-600'}`} />
                              <span className={`text-xs font-medium ${device.plc_ok ? 'text-green-700' : 'text-orange-700'}`}>
                                PLC {device.plc_ok ? 'Online' : 'Offline'}
                              </span>
                            </div>
                            {device.falhas > 0 && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                {device.falhas} falha{device.falhas > 1 ? 's' : ''}
                              </Badge>
                            )}
                            {device.ultima_leitura_plc && (
                              <span className="text-xs text-gray-400 ml-auto">
                                PLC: {format(new Date(device.ultima_leitura_plc), 'HH:mm:ss', { locale: pt })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Estado do elevador - só se disponível */}
                    {device.elevador_estado_disponivel ? (
                      <>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className={`p-2 rounded ${device.em_movimento ? 'bg-blue-100' : 'bg-gray-100'}`}>
                            <Activity className={`h-4 w-4 mx-auto ${device.em_movimento ? 'text-blue-600' : 'text-gray-400'}`} />
                            <p className="text-xs mt-1">{device.em_movimento ? 'Movimento' : 'Parado'}</p>
                          </div>
                          <div className={`p-2 rounded ${device.porta_aberta ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                            <DoorOpen className={`h-4 w-4 mx-auto ${device.porta_aberta ? 'text-yellow-600' : 'text-gray-400'}`} />
                            <p className="text-xs mt-1">{device.porta_aberta ? 'Aberta' : 'Fechada'}</p>
                          </div>
                          <div className={`p-2 rounded ${device.tem_alarme ? 'bg-red-100' : 'bg-green-100'}`}>
                            <AlertTriangle className={`h-4 w-4 mx-auto ${device.tem_alarme ? 'text-red-600' : 'text-green-600'}`} />
                            <p className="text-xs mt-1">{device.tem_alarme ? 'Alarme' : 'Normal'}</p>
                          </div>
                          <div className={`p-2 rounded ${device.chamada ? 'bg-purple-100' : 'bg-gray-100'}`}>
                            <Cpu className={`h-4 w-4 mx-auto ${device.chamada ? 'text-purple-600' : 'text-gray-400'}`} />
                            <p className="text-xs mt-1">{device.chamada ? 'Chamada' : 'Livre'}</p>
                          </div>
                        </div>

                        {/* Lista de alarmes ativos clicáveis (cada um vai à origem) */}
                        {device.tem_alarme && device.alarmes_ativos && (
                          <div className="mt-3 p-3 rounded border border-red-300 bg-red-50/60">
                            <div className="flex items-center gap-1.5 mb-2">
                              <ShieldAlert className="h-3.5 w-3.5 text-red-600" />
                              <span className="text-xs font-semibold text-red-700">Alarmes Ativos · clicar para ver origem</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {Object.entries(device.alarmes_ativos)
                                .filter(([, isActive]) => isActive)
                                .map(([key]) => (
                                  <button
                                    key={key}
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleAlarmClick(device.id, key); }}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-600 text-white text-[11px] font-medium hover:bg-red-700 transition-colors animate-pulse"
                                    data-testid={`elevator-alarm-${device.id}-${key}`}
                                  >
                                    <AlertTriangle className="h-3 w-3" />
                                    {ALARM_LABELS[key] || key}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Dados extra para dados reais com estado disponível */}
                        {!device.simulado && (
                          <div className="grid grid-cols-4 gap-2 text-center mt-2">
                            <div className={`p-1.5 rounded ${device.a_subir ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                              <ArrowUp className={`h-3.5 w-3.5 mx-auto ${device.a_subir ? 'text-blue-600' : 'text-gray-300'}`} />
                              <p className="text-[10px] mt-0.5">Subir</p>
                            </div>
                            <div className={`p-1.5 rounded ${device.a_descer ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                              <ArrowDown className={`h-3.5 w-3.5 mx-auto ${device.a_descer ? 'text-blue-600' : 'text-gray-300'}`} />
                              <p className="text-[10px] mt-0.5">Descer</p>
                            </div>
                            <div className={`p-1.5 rounded ${device.no_piso ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                              <CheckCircle className={`h-3.5 w-3.5 mx-auto ${device.no_piso ? 'text-green-600' : 'text-gray-300'}`} />
                              <p className="text-[10px] mt-0.5">No Piso</p>
                            </div>
                            <div className={`p-1.5 rounded ${device.psg_preso ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                              <Users className={`h-3.5 w-3.5 mx-auto ${device.psg_preso ? 'text-red-600' : 'text-gray-300'}`} />
                              <p className="text-[10px] mt-0.5">Psg. Preso</p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : !device.simulado ? (
                      <div className="p-4 rounded bg-gray-100 border border-gray-200 text-center">
                        <WifiOff className="h-6 w-6 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-600">Estado Indeterminado</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {!device.comunicador_online 
                            ? 'Sem comunicação com o comunicador' 
                            : 'PLC sem resposta — autómato indisponível'}
                        </p>
                      </div>
                    ) : null}
                    
                    {device.last_reading && (
                      <p className="text-xs text-gray-400 mt-2 text-right">
                        Última leitura: {format(new Date(device.last_reading), 'dd/MM HH:mm:ss', { locale: pt })}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Análise Operacional em Tempo Real */}
      {hasDevices && selectedDevice && analiseOperacional && (
        <Card data-testid="analise-operacional">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-500" />
                Análise Operacional em Tempo Real
              </h2>
              <div className="flex items-center gap-2">
                {analiseOperacional.comunicador_online ? (
                  <Badge className="bg-green-100 text-green-700">Comunicador Online</Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700">Comunicador Offline</Badge>
                )}
                {analiseOperacional.plc_ok ? (
                  <Badge className="bg-green-100 text-green-700">PLC Online</Badge>
                ) : (
                  <Badge className="bg-orange-100 text-orange-700">PLC Offline</Badge>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Nº Equipamento */}
              <OperationalItem
                label="Nº Equipamento"
                value={analiseOperacional.analise.equipamento}
                type="text"
              />
              
              {/* Piso Atual */}
              <OperationalItem
                label="Piso Atual"
                value={analiseOperacional.analise.piso_atual}
                type="number"
                unavailableText="Aguarda envio do Raspberry"
              />
              
              {/* Nº Manobras (Travagens) */}
              <OperationalItem
                label="Nº Manobras (Travagens)"
                value={analiseOperacional.analise.num_manobras}
                type="number"
                unavailableText="Aguarda envio do Raspberry"
              />
              
              {/* Comando Fora de Serviço */}
              <OperationalItem
                label="Comando Fora de Serviço"
                value={analiseOperacional.analise.cmd_fora_servico}
                type="alarm"
                trueText="ATIVO"
                falseText="Inativo"
              />
              
              {/* Equipamento em Serviço */}
              <OperationalItem
                label="Equipamento em Serviço"
                value={analiseOperacional.analise.em_servico}
                type="status"
                trueText="Em Serviço"
                falseText="FORA SERVIÇO"
              />
              
              {/* Equipamento em Avaria */}
              <OperationalItem
                label="Equipamento em Avaria"
                value={analiseOperacional.analise.em_avaria}
                type="alarm"
                trueText="AVARIA"
                falseText="Normal"
              />
              
              {/* Telefone Emergência */}
              <OperationalItem
                label="Telefone Emergência Ativa"
                value={analiseOperacional.analise.telefone_emergencia}
                type="alarm"
                trueText="ATIVO"
                falseText="Inativo"
              />
              
              {/* Passageiro Preso */}
              <OperationalItem
                label="Passageiro Preso"
                value={analiseOperacional.analise.passageiro_preso}
                type="critical"
                trueText="ALARME ATIVO"
                falseText="Normal"
              />
              
              {/* Porta Aberta Prolongada */}
              <OperationalItem
                label="Porta Aberta >50s"
                value={analiseOperacional.analise.porta_aberta_prolongada}
                type="alarm"
                trueText="PORTA ABERTA"
                falseText="Normal"
              />
              
              {/* Tensão 230V */}
              <OperationalItem
                label="Tensão 230V"
                value={analiseOperacional.analise.tensao_230v_ok}
                type="status"
                trueText="OK"
                falseText="FALHA"
              />
              
              {/* Temp. Casa Máquinas */}
              <OperationalItem
                label="Temp. Casa Máquinas"
                value={analiseOperacional.analise.temp_casa_maquinas}
                type="alarm"
                trueText="ALARME"
                falseText="Normal"
              />
              
              {/* Manutenção Preventiva */}
              <OperationalItem
                label="Manutenção Preventiva"
                value={analiseOperacional.analise.manutencao_preventiva}
                type="maintenance"
                trueText="EM MANUTENÇÃO"
                falseText="Normal"
              />
              
              {/* Manutenção Corretiva */}
              <OperationalItem
                label="Manutenção Corretiva"
                value={analiseOperacional.analise.manutencao_corretiva}
                type="maintenance"
                trueText="EM MANUTENÇÃO"
                falseText="Normal"
              />
            </div>

            {analiseOperacional.timestamp && (
              <p className="text-xs text-gray-400 mt-4 text-right">
                Dados de: {format(new Date(analiseOperacional.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: pt })}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Histórico de Registos */}
      {hasDevices && selectedDevice && readings15min.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Cpu className="h-5 w-5 text-purple-500" />
                Histórico de Registos (15 min)
              </h2>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Elevador:</Label>
                <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                  <SelectTrigger className="w-[200px]" data-testid="device-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.local}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Verificar se há dados reais ou simulados para decidir colunas */}
            {(() => {
              const hasRealData = readings15min.some(r => isRealReading(r.reading));
              
              if (hasRealData) {
                // Tabela com Tabs para Entradas, Saídas e Estado
                return (
                  <Tabs defaultValue="estado" className="space-y-3">
                    <TabsList>
                      <TabsTrigger value="estado">Estado ({Object.keys(readings15min[0]?.reading?.estado || {}).length || 0})</TabsTrigger>
                      <TabsTrigger value="entradas">Entradas ({Object.keys(readings15min[0]?.reading?.entradas || {}).length || 0})</TabsTrigger>
                      <TabsTrigger value="saidas">Saídas</TabsTrigger>
                    </TabsList>

                    <TabsContent value="estado">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="sticky left-0 bg-white z-10">Data/Hora</TableHead>
                              <TableHead>PLC</TableHead>
                              <TableHead>Fora Serv.</TableHead>
                              <TableHead>Movimento</TableHead>
                              <TableHead>Avaria</TableHead>
                              <TableHead>Piso</TableHead>
                              <TableHead>Subir</TableHead>
                              <TableHead>Descer</TableHead>
                              <TableHead>Chamada</TableHead>
                              <TableHead>Psg. Preso</TableHead>
                              <TableHead>Porta 50s</TableHead>
                              <TableHead>Emg. 3s</TableHead>
                              <TableHead>AC OK</TableHead>
                              <TableHead>Bat. Baixa</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {readings15min.map((record, index) => {
                              const r = record.reading;
                              const estado = r.estado || {};
                              return (
                                <TableRow key={index}>
                                  <TableCell className="font-mono text-sm sticky left-0 bg-white z-10">
                                    {format(new Date(record.timestamp), 'dd/MM HH:mm', { locale: pt })}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={r.plc_ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                      {r.plc_ok ? 'ON' : 'OFF'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_FORA_SERV} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_MOV} trueLabel="SIM" falseLabel="NÃO" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_AVARIA} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_PISO} trueLabel="SIM" falseLabel="NÃO" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_SUBIR} trueLabel="SIM" falseLabel="NÃO" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_DESCER} trueLabel="SIM" falseLabel="NÃO" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_CHAMADA} trueLabel="SIM" falseLabel="NÃO" activeColor="purple" /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_PSG_PRESO} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_PORTA_50S} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={estado.EQUIP1_BOT_EMG_3S} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={estado.INF_AC_OK} trueLabel="OK" falseLabel="FALHA" /></TableCell>
                                  <TableCell><BoolBadge value={estado.INF_BAT_LOW} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>

                    <TabsContent value="entradas">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="sticky left-0 bg-white z-10">Data/Hora</TableHead>
                              <TableHead>Fora Serv.</TableHead>
                              <TableHead>Movimento</TableHead>
                              <TableHead>Chamada C2000</TableHead>
                              <TableHead>Porta</TableHead>
                              <TableHead>Emg.</TableHead>
                              <TableHead>Mag1</TableHead>
                              <TableHead>Mag2</TableHead>
                              <TableHead>Luz</TableHead>
                              <TableHead>Man. Prev.</TableHead>
                              <TableHead>Man. Corr.</TableHead>
                              <TableHead>Modo Norm.</TableHead>
                              <TableHead>Rel. Fases</TableHead>
                              <TableHead>Rel. Térm.</TableHead>
                              <TableHead>Falha Bat.</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {readings15min.map((record, index) => {
                              const ent = record.reading.entradas || {};
                              return (
                                <TableRow key={index}>
                                  <TableCell className="font-mono text-sm sticky left-0 bg-white z-10">
                                    {format(new Date(record.timestamp), 'dd/MM HH:mm', { locale: pt })}
                                  </TableCell>
                                  <TableCell><BoolBadge value={ent.FORA_SERV_ELEV1} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={ent.MOV_ELEV1} trueLabel="SIM" falseLabel="NÃO" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={ent.CHAMADA_C2000_ELEV1} trueLabel="SIM" falseLabel="NÃO" activeColor="purple" /></TableCell>
                                  <TableCell><BoolBadge value={ent.PORTA_ELEV1} trueLabel="ABERTA" falseLabel="FECHADA" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={ent.BOT_EMG_ELEV1} trueLabel="ATIVO" falseLabel="OK" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={ent.SENS_MAG1_ELEV1} trueLabel="ON" falseLabel="OFF" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={ent.SENS_MAG2_ELEV1} trueLabel="ON" falseLabel="OFF" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={ent.SENS_LUZ_ELEV1} trueLabel="ON" falseLabel="OFF" activeColor="blue" /></TableCell>
                                  <TableCell><BoolBadge value={ent.MAN_PREV_ELEV1} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={ent.MAN_CORR_ELEV1} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={ent.MOD_NORM_ELEV1} trueLabel="SIM" falseLabel="NÃO" /></TableCell>
                                  <TableCell><BoolBadge value={ent.REL_FASES_ELEV1} trueLabel="OK" falseLabel="FALHA" /></TableCell>
                                  <TableCell><BoolBadge value={ent.REL_TERM_ELEV1} trueLabel="ATIVO" falseLabel="OK" invertColor /></TableCell>
                                  <TableCell><BoolBadge value={ent.FALHA_BAT_ELEV1} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>

                    <TabsContent value="saidas">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Data/Hora</TableHead>
                              <TableHead>CMD Fora Serviço</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {readings15min.map((record, index) => {
                              const saidas = record.reading.saidas || {};
                              return (
                                <TableRow key={index}>
                                  <TableCell className="font-mono text-sm">
                                    {format(new Date(record.timestamp), 'dd/MM HH:mm', { locale: pt })}
                                  </TableCell>
                                  <TableCell><BoolBadge value={saidas.CMD_FORA_SERV_ELEV1} trueLabel="ATIVO" falseLabel="INATIVO" invertColor /></TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                  </Tabs>
                );
              }
              
              // Tabela simplificada para dados simulados (formato antigo com registos)
              return (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Hora</TableHead>
                        <TableHead>Fora Serviço</TableHead>
                        <TableHead>Movimento</TableHead>
                        <TableHead>Porta</TableHead>
                        <TableHead>Emergência</TableHead>
                        <TableHead>Modo Normal</TableHead>
                        <TableHead>AC OK</TableHead>
                        <TableHead>Bat. Baixa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {readings15min.map((record, index) => {
                        const reg = record.reading.registos || {};
                        return (
                          <TableRow key={index}>
                            <TableCell className="font-mono text-sm">
                              {format(new Date(record.timestamp), 'dd/MM HH:mm', { locale: pt })}
                            </TableCell>
                            <TableCell><BoolBadge value={reg.FORA_SERV_ELEV1} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                            <TableCell><BoolBadge value={reg.MOV_ELEV1} trueLabel="SIM" falseLabel="NÃO" activeColor="blue" /></TableCell>
                            <TableCell><BoolBadge value={reg.PORTA_ELEV1} trueLabel="ABERTA" falseLabel="FECHADA" invertColor /></TableCell>
                            <TableCell><BoolBadge value={reg.BOT_EMG_ELEV1} trueLabel="ATIVO" falseLabel="OK" invertColor /></TableCell>
                            <TableCell><BoolBadge value={reg.MOD_NORM_ELEV1} trueLabel="SIM" falseLabel="NÃO" /></TableCell>
                            <TableCell><BoolBadge value={reg.AC_OK} trueLabel="OK" falseLabel="FALHA" /></TableCell>
                            <TableCell><BoolBadge value={reg.BAT_LOW} trueLabel="SIM" falseLabel="NÃO" invertColor /></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Tabela filtrável de eventos + exportação */}
      {hasDevices && (
        <Card data-testid="elevadores-events-table-card" id="elevadores-events-section">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-500" />
                  Registos de Eventos
                  <Badge className="ml-1 bg-green-100 text-green-800">Dados Reais/Simulados</Badge>
                </h2>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Filter className="h-4 w-4" />
                    <span>{(() => {
                      const f = eventReadings.filter((r) =>
                        (eventDevice === 'all' || r.elevator_id === eventDevice) &&
                        (!eventOnlyAlarms || r.tem_alarme) &&
                        (eventSearch === '' ||
                          r.local?.toLowerCase().includes(eventSearch.toLowerCase()) ||
                          r.equipamento?.toLowerCase().includes(eventSearch.toLowerCase()) ||
                          (r.timestamp || '').includes(eventSearch))
                      );
                      return `${f.length} registos`;
                    })()}</span>
                  </div>
                  <ExportMenu
                    columns={[
                      { key: 'timestamp', label: 'Data/Hora', type: 'datetime' },
                      { key: 'local', label: 'Local' },
                      { key: 'equipamento', label: 'Equipamento' },
                      { key: 'plc_ok', label: 'PLC OK', value: (r) => r.plc_ok ? 'Sim' : 'Não' },
                      { key: 'em_movimento', label: 'Movimento', value: (r) => r.em_movimento ? 'Sim' : 'Não' },
                      { key: 'porta_aberta', label: 'Porta Aberta', value: (r) => r.porta_aberta ? 'Sim' : 'Não' },
                      { key: 'fora_servico', label: 'Fora Serviço', value: (r) => r.fora_servico ? 'Sim' : 'Não' },
                      { key: 'tem_alarme', label: 'Alarme', value: (r) => r.tem_alarme ? 'Sim' : 'Não' },
                      { key: 'falhas', label: 'Falhas', type: 'number', decimals: 0 },
                    ]}
                    rows={eventReadings.filter((r) =>
                      (eventDevice === 'all' || r.elevator_id === eventDevice) &&
                      (!eventOnlyAlarms || r.tem_alarme) &&
                      (eventSearch === '' ||
                        r.local?.toLowerCase().includes(eventSearch.toLowerCase()) ||
                        r.equipamento?.toLowerCase().includes(eventSearch.toLowerCase()) ||
                        (r.timestamp || '').includes(eventSearch))
                    )}
                    filename="eventos_elevadores"
                    title="Registo de Eventos — Elevadores"
                    subtitle={`Janela: últimas ${eventHoras}h · ${eventOnlyAlarms ? 'Apenas alarmes' : 'Todos os eventos'}`}
                    testId="elevadores-export"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Pesquisar..."
                    value={eventSearch}
                    onChange={(e) => setEventSearch(e.target.value)}
                    className="pl-10"
                    data-testid="elevadores-events-search"
                  />
                </div>
                <Select value={eventDevice} onValueChange={setEventDevice}>
                  <SelectTrigger data-testid="elevadores-events-filter-device">
                    <SelectValue placeholder="Todos os elevadores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os elevadores</SelectItem>
                    {devices.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.local}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(eventHoras)} onValueChange={(v) => setEventHoras(parseInt(v))}>
                  <SelectTrigger data-testid="elevadores-events-window">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">Últimas 6h</SelectItem>
                    <SelectItem value="24">Últimas 24h</SelectItem>
                    <SelectItem value="48">Últimas 48h</SelectItem>
                    <SelectItem value="168">Última semana</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center justify-end gap-2">
                  <Switch
                    id="only-alarms"
                    checked={eventOnlyAlarms}
                    onCheckedChange={setEventOnlyAlarms}
                    data-testid="elevadores-events-only-alarms"
                  />
                  <Label htmlFor="only-alarms" className="text-sm cursor-pointer">Apenas alarmes</Label>
                </div>
              </div>

              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Local</TableHead>
                      <TableHead>Equipamento</TableHead>
                      <TableHead className="text-center">PLC</TableHead>
                      <TableHead className="text-center">Mov.</TableHead>
                      <TableHead className="text-center">Porta</TableHead>
                      <TableHead className="text-center">F.Serv.</TableHead>
                      <TableHead className="text-center">Alarme</TableHead>
                      <TableHead className="text-right">Falhas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const filtered = eventReadings.filter((r) =>
                        (eventDevice === 'all' || r.elevator_id === eventDevice) &&
                        (!eventOnlyAlarms || r.tem_alarme) &&
                        (eventSearch === '' ||
                          r.local?.toLowerCase().includes(eventSearch.toLowerCase()) ||
                          r.equipamento?.toLowerCase().includes(eventSearch.toLowerCase()) ||
                          (r.timestamp || '').includes(eventSearch))
                      );
                      if (filtered.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                              Sem eventos no período selecionado.
                            </TableCell>
                          </TableRow>
                        );
                      }
                      const Dot = ({ on, color }) => (
                        <span className={`inline-block w-2 h-2 rounded-full ${on ? color : 'bg-gray-200'}`} />
                      );
                      return filtered.slice(0, 500).map((row, idx) => {
                        const isHighlighted = highlightTimestamp && row.timestamp === highlightTimestamp;
                        return (
                        <TableRow
                          key={`${row.elevator_id}-${row.timestamp}-${idx}`}
                          className={`hover:bg-gray-50 ${isHighlighted ? 'bg-yellow-100 ring-2 ring-yellow-400' : row.tem_alarme ? 'bg-red-50/40' : ''}`}
                          ref={isHighlighted ? (node) => { if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' }); } : undefined}
                        >
                          <TableCell className="font-mono text-xs">
                            {row.timestamp ? format(new Date(row.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: pt }) : '—'}
                          </TableCell>
                          <TableCell className="font-medium">{row.local}</TableCell>
                          <TableCell className="font-mono text-xs text-gray-600">{row.equipamento}</TableCell>
                          <TableCell className="text-center"><Dot on={row.plc_ok} color="bg-emerald-500" /></TableCell>
                          <TableCell className="text-center"><Dot on={row.em_movimento} color="bg-blue-500" /></TableCell>
                          <TableCell className="text-center"><Dot on={row.porta_aberta} color="bg-amber-500" /></TableCell>
                          <TableCell className="text-center"><Dot on={row.fora_servico} color="bg-orange-500" /></TableCell>
                          <TableCell className="text-center"><Dot on={row.tem_alarme} color="bg-red-500" /></TableCell>
                          <TableCell className="text-right font-mono tabular-nums">{row.falhas}</TableCell>
                        </TableRow>
                      );
                      });
                    })()}
                  </TableBody>
                </Table>
                {(() => {
                  const filtered = eventReadings.filter((r) =>
                    (eventDevice === 'all' || r.elevator_id === eventDevice) &&
                    (!eventOnlyAlarms || r.tem_alarme)
                  );
                  if (filtered.length > 500) {
                    return (
                      <div className="text-xs text-gray-500 text-center py-2 bg-gray-50">
                        A mostrar 500 dos {filtered.length} registos. Use a exportação para obter o conjunto completo.
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mensagem para configurar */}
      {!hasDevices && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-6 text-center" data-testid="no-devices-message">
            <MoveVertical className="h-12 w-12 mx-auto mb-4 text-purple-400" />
            <h3 className="text-lg font-semibold text-gray-900">Configure os seus elevadores</h3>
            <p className="text-gray-600 mt-2 mb-4">
              Adicione elevadores com autómatos Schneider TM221 para monitorização em tempo real
            </p>
            <Button onClick={() => navigate('/configuracoes/elevadores')}>
              <Settings className="h-4 w-4 mr-2" />
              Configurar Elevadores
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// Componente para item da análise operacional
const OperationalItem = ({ label, value, type = 'text', trueText = 'SIM', falseText = 'NÃO', unavailableText = 'N/D' }) => {
  const renderValue = () => {
    if (value === null || value === undefined) {
      return <span className="text-sm text-gray-400 italic">{unavailableText}</span>;
    }
    
    if (type === 'text') {
      return <span className="text-sm font-semibold text-gray-900">{value}</span>;
    }
    
    if (type === 'number') {
      return <span className="text-lg font-bold text-purple-700">{value}</span>;
    }
    
    // Boolean types: status, alarm, critical, maintenance
    const isActive = Boolean(value);
    
    if (type === 'critical') {
      return (
        <Badge className={isActive ? 'bg-red-600 text-white animate-pulse' : 'bg-green-100 text-green-700'}>
          {isActive ? trueText : falseText}
        </Badge>
      );
    }
    
    if (type === 'alarm') {
      return (
        <Badge className={isActive ? 'bg-red-100 text-red-700 font-bold' : 'bg-green-100 text-green-700'}>
          {isActive ? trueText : falseText}
        </Badge>
      );
    }
    
    if (type === 'maintenance') {
      return (
        <Badge className={isActive ? 'bg-yellow-100 text-yellow-800 font-bold' : 'bg-green-100 text-green-700'}>
          {isActive ? trueText : falseText}
        </Badge>
      );
    }
    
    // type === 'status'
    return (
      <Badge className={isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700 font-bold'}>
        {isActive ? trueText : falseText}
      </Badge>
    );
  };
  
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-white">
      <span className="text-sm text-gray-600">{label}</span>
      {renderValue()}
    </div>
  );
};

// Componente auxiliar para badges booleanos
const BoolBadge = ({ value, trueLabel = 'SIM', falseLabel = 'NÃO', invertColor = false, activeColor = 'green' }) => {
  if (value === null || value === undefined) {
    return <Badge className="bg-gray-100 text-gray-500">-</Badge>;
  }
  
  const colorMap = {
    green: { active: 'bg-green-100 text-green-700', inactive: 'bg-gray-100 text-gray-600' },
    blue: { active: 'bg-blue-100 text-blue-700', inactive: 'bg-gray-100 text-gray-600' },
    purple: { active: 'bg-purple-100 text-purple-700', inactive: 'bg-gray-100 text-gray-600' },
  };
  
  const colors = colorMap[activeColor] || colorMap.green;
  
  if (invertColor) {
    // true = problema (vermelho), false = ok (verde)
    return (
      <Badge className={value ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
        {value ? trueLabel : falseLabel}
      </Badge>
    );
  }
  
  // true = ativo (cor do activeColor), false = inativo (cinza)
  return (
    <Badge className={value ? colors.active : colors.inactive}>
      {value ? trueLabel : falseLabel}
    </Badge>
  );
};

export default Elevadores;
